// src/services/WorkspaceService.mjs
import apiClient from "../config/axiosConfig.js";

/**
 * Fetches all workspaces the currently authenticated user belongs to.
 */
export async function getAllWorkspacesForUser() {
    const response = await apiClient.get("/api/workspaces/get-all-for-user");
    return response.data; // Workspace[]
}

/**
 * Checks whether the current user is allowed to create a new workspace.
 * Returns { allowed: boolean, reason?: string }
 */
export async function canCreateWorkspace() {
    const response = await apiClient.get("/api/workspaces/can-create");
    return response.data;
}

/**
 * Creates a new workspace with the given name.
 * Returns the created Workspace object.
 */
export async function createWorkspace(name) {
    const response = await apiClient.post("/api/workspaces/create", { name });
    return response.data; // Workspace
}

/**
 * Renames the current context workspace (admin+).
 */
export async function renameWorkspace(name) {
    const response = await apiClient.post("/api/workspaces/rename", { name });
    return response.data;
}

/**
 * Fetches all members of the current context workspace.
 */
export async function getWorkspaceMembers() {
    const response = await apiClient.get("/api/workspaces/members");
    return response.data; // User[]
}

/**
 * Adds a user to the workspace using their UID.
 */
export async function addUserToWorkspace(targetUserId, role = 'viewer') {
    const response = await apiClient.post("/api/workspaces/add-user", { targetUserId, role });
    return response.data;
}

/**
 * Removes a user from the workspace.
 */
export async function removeUserFromWorkspace(targetUserId) {
    const response = await apiClient.post("/api/workspaces/remove-user", { targetUserId });
    return response.data;
}

/**
 * Promotes/demotes an existing member (admin+).
 *
 * The backend endpoint, its route, and its escalation guards have existed since
 * be-workspace-1 — but nothing ever called them, so changing someone's role meant
 * removing and re-adding them. This is the missing half.
 *
 * The backend refuses a role at or above the caller's own rank, and refuses to
 * touch the owner's role at all (that needs ownership transfer).
 */
export async function changeMemberRole(targetUserId, role) {
    const response = await apiClient.post("/api/workspaces/change-member-role", { targetUserId, role });
    return response.data;
}

/**
 * Transfers ownership of the current workspace to another member (owner only).
 *
 * `confirmName` must match the workspace name — the transfer is irreversible from
 * the initiator's side, since taking it back needs the new owner's cooperation.
 * The previous owner is demoted to admin, not removed.
 */
export async function transferOwnership(targetUserId, confirmName) {
    const response = await apiClient.post("/api/workspaces/transfer-ownership", { targetUserId, confirmName });
    return response.data;
}

/**
 * The workspace activity feed. Any member may read it; only team-visible events
 * are returned (a personal security event never appears here).
 */
export async function getWorkspaceActivity(limit = 50) {
    const response = await apiClient.get("/api/workspaces/activity", { params: { limit } });
    return response.data.events;
}